import React from 'react'
import LoadingBanner from '../../components/LoadingBanner'

import ProjectBiddingWizardStep from './BiddingWizardStep'
import BiddingContractStep from './BiddingContractStep'
import BiddingTalentStep from './BiddingTalentStep'
import Project from "../../models/Project";
import {BiddingWizard} from "./BiddingWizard";
import BiddingBriefStep from "./BiddingBriefStep";
import {useProject} from "../../providers/ClientProjectProvider";
import {useLocation, useNavigate, useOutletContext} from "react-router-dom";
import {useIsMutating} from "@tanstack/react-query";
import ProjectShortlistedTeamCandidates from "./ProjectShortlistedTeamCandidates";
import ProposalFooter from './components/ProposalFooter';
import ClientLeadManagementTools from "../client-lead-tools/ClientLeadManagementTools";

export default class BiddingProposalsStep extends ProjectBiddingWizardStep {
    static slug = 'shortlist';

    static path = '/shortlist';

    static StepTitle = 'Proposals';
    static StepHeader = 'Review and accept team proposal';
    static accomplishedForProject(project) {
        return project.hasRequestedProposal();
    }

    static stepTitleFor(project) {
        if (!project) {
            return;
        }

        if (project.isService()) {
            return 'Proposals';
        }

        return 'Proposal';
    }

    static stepBadgeFor(project) {
        return project.shortlisted_candidates_count || undefined;
    }
    /**
     * [Notes]
     *
     * @param {Object} props
     * @param context
     * @returns void
     */
    constructor(props, context = undefined)
    {
        super(props, context)

        this.state = {
            isLoading: true
        };
    }

    componentDidMount() {
        super.componentDidMount();

        if (!this.props.isDisabled) {
            this.middlewareCheck(() => {
                this.setState({isLoading: false})
            })
        }
    }

    async middlewareCheck(callback) {
        if (!this._isMounted) return;

        if (!this.context?.auth?.canViewTalentForProject(this.props.project)) {
            await TB.sysAlert('client.tab_visit_project_permission_error');
            await TB.wait(500);

            return this.props.goToStep(BiddingBriefStep);
        }

        if (this.props.queryParams?.skipCheck) {
            return callback()
        }

        // if (!this.props.project.hasConfirmedProjectScope()) {
        //     await TB.sysAlert('team_shopping.proposal_tab_visit_proposal_not_requested');
        //     await TB.wait(500);
        //
        //     return this.props.goToStep(BiddingTalentStep);
        // }else
        if (!this.props.project.hasRequestedProposal() && !this.props.project.isActive()) {
            await TB.sysAlert('team_shopping.contract_step_error_no_requested_proposals');
            await TB.wait(500);

            return this.props.goToStep(BiddingTalentStep);
        }

        // Proposal SOW In Progress
        if (this.props.project.hasReceivedProposal() &&
            this.props.project.hasRequestedSOW() && !this.props.project.isPendingContract()
            && !this.props.project.isActive()
        ) {
            if (this.props.queryParams?.from) {
                await TB.wait(200);
            }

            await TB.sysAlert('team_shopping.proposal_tab_visit_sow_in_progress');
        }

        return callback()
    }

    async onAssignTeam(team) {
        if (!team.can_be_assigned) {
            return TB.sysAlert('team_shopping.cant_select_uninterested_team');
        }

        let needsEngagementLead = this.props.project.missingNeededEngagementLead();

        if (needsEngagementLead) {
            return TB.sysAlert('team_shopping.request_proposal_missing_engagement_lead');
        }

        await this.props.toggleUsability(false);

        let updatedTeam = await team.assignTo(this.props.project);

        if (!updatedTeam) {
            await this.props.toggleUsability(true);

            // handle error
            return;
        }


        // await TB.sysAlert('team_shopping.assign_team_button_clicked')
        await TB.sysAlert('team_shopping.request_sow_button_clicked');

        await this.props.toggleUsability(true);
    }

    // async onUnassignTeam(team)
    // {
    // 	await this.props.toggleUsability(false);
    //
    // 	let updatedTeam = await team.unassignFrom(this.props.project);
    //
    // 	if (!updatedTeam) {
    //         await this.props.toggleUsability(true);
    //
    //         // handle error
    // 		return;
    // 	}
    //
    // 	await TB.sysAlert('team_shopping.unassign_team_button_clicked');
    //
    //     await this.props.toggleUsability(true);
    // }

    async onRequestInterview(team) {
        await this.props.toggleUsability(false);

        let updatedTeam = await team.requestInterview(this.props.project);

        if (!updatedTeam) {
            await this.props.toggleUsability(true);

            // handle error
            return;
        }

        await TB.sysAlert('team_shopping.request_interview_button_clicked');

        await this.props.toggleUsability(true);
    }

    toggleCoverLetterModal(shouldShow) {
        return this.promisedState({
            showCoverLetterModal: TB.isBoolean(shouldShow)
                ? shouldShow
                : !this.state.showCoverLetterModal,
        });
    }

    async showCoverLetterModal(talent) {
        await this.promisedState({
            selectedTalent: talent,
        });

        return this.toggleCoverLetterModal(true);
    }

    goToTalentMatching() {
        this.props.goToStep(BiddingTalentStep);
    }

    async onRequest(request) {
        if (this.props.isDisabled) return;

        await this.props.toggleUsability(false);

        let response = await request();

        await this.props.toggleUsability(true);

        return response;
    }

    async cancelProposalRequest() {
        // console.log('BiddingProposalsStep @ cancelProposalRequest');
        this.props.toggleUsability(false);

        let wasSuccess = await (Project.make({ ...this.props.project })).cancelProposalRequest();

        if (!wasSuccess) {
            this.props.toggleUsability(true);
            return;
        }

        this.props.toggleUsability(true);
    }

    async requestSOW() {
        this.props.toggleUsability(false);

        let wasSuccess = await this.props.project.requestSOW();

        this.props.toggleUsability(true);

        if (!wasSuccess) {
            return;
        }

        await TB.sysAlert('team_shopping.request_sow_button_clicked');
    }


    render() {

        if (this.state.isLoading) {
            return (
                <div className="flex-grow flex flex-col items-stretch justify-start py-10 px-2 sm:px-6">
                    {!this.props.location.pathname.includes('profiles') && this.renderStepHeader()}
                    {!this.props.location.pathname.includes('profiles') && <hr className="project-bidding-step-divider" />}

                    <LoadingBanner message={this.props.location.pathname.includes('profiles') ? "Loading" : "Finding Matches"} />
                </div>
            )
        }

        let biddingPath = `${BiddingWizard.pathFor(this.props.project.id, this.constructor.slug)}`;

        return (
            <div className={TB.mapClass("py-10 flex flex-col items-stretch gap-6 flex-grow px-2 sm:px-6", {
                "opacity-50 pointer-events-none": this.props.isUpdating
            })}>

                {this.context.auth?.canManageClients() && <ClientLeadManagementTools/>}

                <ProjectShortlistedTeamCandidates
                    isDisabled={this.props.isDisabled}
                    onRequest={this.onRequest.bind(this)}
                    onUpdated={this.refreshTalentFor.bind(this)}
                >
                    {this.props.project.canRequestSOW() && !this.props.project.hasRequestedSOW() && (
                        <button type="button"
                            className={TB.mapClass("btn btn-primary mr-3", {
                                'disabled-edit-link': !this.context?.auth?.canRequestSOWForProject(this.props.project)
                            })}
                            onClick={() => {
                                if (!this.context?.auth?.canRequestSOWForProject(this.props.project)) {
                                    return TB.showPermissionError()
                                }

                                return this.requestSOW()
                            }}
                            disabled={this.props.isDisabled}>
                            Request SOW
                            {!this.context?.auth?.canRequestSOWForProject(this.props.project) && (<i className="fa fa-lock ml-1" />)}

                        </button>
                    )}

                    {this.props.project.hasRequestedSOW()
                        && !this.props.project.isPendingContract() && (
                            <button type="button"
                                className="btn btn-primary mr-3 text-disabled-primary"
                                disabled={true}>
                                SOW in Progress
                            </button>
                        )}

                    {this.props.project.isPendingContract() && (
                        <button type="button"
                            disabled={this.props.isDisabled}
                            className={TB.mapClass("btn btn-primary mr-3", {
                                'disabled-edit-link': !this.context?.auth?.canViewSOWForProject(this.props.project)
                            })}
                            onClick={() => {
                                if (!this.context?.auth?.canViewSOWForProject(this.props.project)) {
                                    return TB.showPermissionError()
                                }

                                return this.props.goToStep(BiddingContractStep)
                            }}
                        >
                            View SOW
                            {!this.context?.auth?.canViewSOWForProject(this.props.project) && (<i className="fa fa-lock ml-1" />)}

                        </button>
                    )}

                </ProjectShortlistedTeamCandidates>
            </div>
        )
    }
}

const TeamProposalsWizardStep = (props) => {
    const { project, update, isProjectUpdating } = useProject();

    const location = useLocation();
    const navigate = useNavigate();
    const {
        toggleUsability,
        queryParams,
        isDisabled,
        goToStep
    } = useOutletContext();

    const candidateRequestsRunning = useIsMutating({
        mutationKey: "teamCandidateRequest",
        fetching: true
    })

    if (!project) return null;

    let onRequest = async (callback = async () => true) => {
        await toggleUsability(false);
        let response = await callback();
        await toggleUsability(true);
        return response;
    }

    return (
        <>
            <BiddingProposalsStep
                project={project}
                navigate={navigate}
                updateProject={update}
                location={location}
                onRequest={onRequest}
                toggleUsability={toggleUsability}
                queryParams={queryParams}
                isDisabled={isDisabled || candidateRequestsRunning > 0}
                goToStep={goToStep}
            />
            <ProposalFooter />
        </>
    )
}

export {
    TeamProposalsWizardStep
}